import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Divider,
  Chip,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  Visibility,
  Refresh,
  Print,
  Email,
  Search,
  Clear,
  CreditCard,
  AccountBalance,
  Check,
  CheckCircle,
} from '@mui/icons-material';
import { DataTable, StatusChip } from '../components';
import api from '../services/api';

const AttachMoney = () => <span>$</span>;

const Payments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [methodFilter, setMethodFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Stats
  const [stats, setStats] = useState({
    totalProcessed: 0,
    totalToday: 0,
    totalThisMonth: 0,
    creditCardTotal: 0,
  });

  useEffect(() => {
    fetchPayments();
    fetchStats();
  }, [methodFilter, statusFilter, startDate, endDate]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const params = {};

      if (methodFilter !== 'all') params.payment_method = methodFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await api.get('/payments', { params });
      setPayments(response.data || []);
    } catch (error) {
      console.error('Error fetching payments:', error);
      showSnackbar('Failed to load payments', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/payments/stats');
      setStats(response.data || {
        totalProcessed: 0,
        totalToday: 0,
        totalThisMonth: 0,
        creditCardTotal: 0,
      });
    } catch (error) {
      console.error('Error fetching payment stats:', error);
    }
  };

  const handleViewDetails = async (payment) => {
    try {
      const response = await api.get(`/payments/${payment.id}`);
      setSelectedPayment(response.data);
      setDetailModalOpen(true);
    } catch (error) {
      console.error('Error fetching payment details:', error);
      showSnackbar('Failed to load payment details', 'error');
    }
  };

  const handleRefund = async (paymentId) => {
    if (!window.confirm('Are you sure you want to refund this payment?')) return;

    try {
      await api.post(`/payments/${paymentId}/refund`);
      showSnackbar('Payment refunded successfully', 'success');
      fetchPayments();
      fetchStats();
      setDetailModalOpen(false);
    } catch (error) {
      console.error('Error refunding payment:', error);
      showSnackbar(error.response?.data?.detail || 'Failed to refund payment', 'error');
    }
  };

  const handlePrintReceipt = async (paymentId) => {
    try {
      const response = await api.get(`/payments/${paymentId}/receipt`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `receipt-${paymentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showSnackbar('Receipt downloaded successfully', 'success');
    } catch (error) {
      console.error('Error downloading receipt:', error);
      showSnackbar('Failed to download receipt', 'error');
    }
  };

  const handleEmailReceipt = async (paymentId) => {
    try {
      await api.post(`/payments/${paymentId}/send-receipt`);
      showSnackbar('Receipt sent successfully', 'success');
    } catch (error) {
      console.error('Error sending receipt:', error);
      showSnackbar('Failed to send receipt', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'credit_card':
        return <CreditCard fontSize="small" />;
      case 'cash':
        return <AttachMoney />;
      case 'check':
        return <Check fontSize="small" />;
      case 'bank_transfer':
        return <AccountBalance fontSize="small" />;
      default:
        return null;
    }
  };

  const getPaymentMethodLabel = (method) => {
    return method?.replace('_', ' ').toUpperCase() || 'N/A';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      case 'refunded':
        return 'default';
      default:
        return 'default';
    }
  };

  const filteredPayments = useMemo(() => {
    return payments.filter(payment => {
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch =
        payment.transaction_id?.toLowerCase().includes(searchLower) ||
        payment.invoice_number?.toLowerCase().includes(searchLower) ||
        payment.client_name?.toLowerCase().includes(searchLower);

      return matchesSearch;
    });
  }, [payments, searchQuery]);

  const columns = [
    {
      field: 'transaction_id',
      headerName: 'Transaction ID',
      sortable: true,
      searchable: true,
      minWidth: 150,
    },
    {
      field: 'invoice_number',
      headerName: 'Invoice',
      sortable: true,
      searchable: true,
      minWidth: 120,
    },
    {
      field: 'client_name',
      headerName: 'Client',
      sortable: true,
      searchable: true,
      minWidth: 180,
    },
    {
      field: 'amount',
      headerName: 'Amount',
      sortable: true,
      align: 'right',
      minWidth: 120,
      render: (row) => `$${Number(row.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    },
    {
      field: 'payment_method',
      headerName: 'Method',
      sortable: true,
      minWidth: 140,
      render: (row) => (
        <Box display="flex" alignItems="center">
          {getPaymentMethodIcon(row.payment_method)}
          <Typography variant="body2" sx={{ ml: 1 }}>
            {getPaymentMethodLabel(row.payment_method)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      sortable: true,
      minWidth: 120,
      render: (row) => (
        <Chip
          label={row.status}
          color={getStatusColor(row.status)}
          size="small"
          sx={{ textTransform: 'capitalize' }}
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Date',
      sortable: true,
      minWidth: 160,
      render: (row) => row.created_at ? new Date(row.created_at).toLocaleString() : 'N/A',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      align: 'center',
      minWidth: 150,
      render: (row) => (
        <Stack direction="row" spacing={0.5} justifyContent="center">
          <Tooltip title="View Details">
            <IconButton size="small" onClick={() => handleViewDetails(row)}>
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Print Receipt">
            <IconButton size="small" onClick={() => handlePrintReceipt(row.id)}>
              <Print fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Email Receipt">
            <IconButton size="small" onClick={() => handleEmailReceipt(row.id)}>
              <Email fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <div>
          <Typography variant="h4" fontWeight="bold">
            Payment History
          </Typography>
          <Typography variant="body1" color="text.secondary">
            View and manage all payment transactions
          </Typography>
        </div>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={() => {
            fetchPayments();
            fetchStats();
          }}
        >
          Refresh
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Processed
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="primary">
                ${stats.totalProcessed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Today
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                ${stats.totalToday.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                This Month
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="info.main">
                ${stats.totalThisMonth.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Credit Card Total
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                ${stats.creditCardTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by transaction ID, invoice, or client..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                  endAdornment: searchQuery && (
                    <IconButton size="small" onClick={() => setSearchQuery('')}>
                      <Clear fontSize="small" />
                    </IconButton>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={methodFilter}
                  label="Payment Method"
                  onChange={(e) => setMethodFilter(e.target.value)}
                >
                  <MenuItem value="all">All Methods</MenuItem>
                  <MenuItem value="credit_card">Credit Card</MenuItem>
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="check">Check</MenuItem>
                  <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                  <MenuItem value="refunded">Refunded</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2.5}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From Date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.5}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To Date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Payments Table */}
      <DataTable
        columns={columns}
        data={filteredPayments}
        loading={loading}
        searchable={false}
        emptyMessage="No payments found"
        defaultRowsPerPage={25}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />

      {/* Payment Detail Modal */}
      <Dialog
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        {selectedPayment && (
          <>
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">Payment Details</Typography>
                <Chip
                  label={selectedPayment.status}
                  color={getStatusColor(selectedPayment.status)}
                  size="small"
                  sx={{ textTransform: 'capitalize' }}
                />
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Transaction ID
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedPayment.transaction_id}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Invoice Number
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedPayment.invoice_number}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Client
                  </Typography>
                  <Typography variant="body1">
                    {selectedPayment.client_name}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Payment Method
                  </Typography>
                  <Box display="flex" alignItems="center">
                    {getPaymentMethodIcon(selectedPayment.payment_method)}
                    <Typography variant="body1" sx={{ ml: 1 }}>
                      {getPaymentMethodLabel(selectedPayment.payment_method)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Amount
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="success.main">
                    ${selectedPayment.amount.toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Date & Time
                  </Typography>
                  <Typography variant="body1">
                    {new Date(selectedPayment.created_at).toLocaleString()}
                  </Typography>
                </Grid>
                {selectedPayment.check_number && (
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Check Number
                    </Typography>
                    <Typography variant="body1">
                      {selectedPayment.check_number}
                    </Typography>
                  </Grid>
                )}
                {selectedPayment.reference_number && (
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Reference Number
                    </Typography>
                    <Typography variant="body1">
                      {selectedPayment.reference_number}
                    </Typography>
                  </Grid>
                )}
                {selectedPayment.card_last_four && (
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Card Details
                    </Typography>
                    <Typography variant="body1">
                      •••• {selectedPayment.card_last_four}
                    </Typography>
                  </Grid>
                )}
                {selectedPayment.notes && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Notes
                    </Typography>
                    <Typography variant="body1">
                      {selectedPayment.notes}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setDetailModalOpen(false)}>Close</Button>
              <Button
                startIcon={<Print />}
                onClick={() => handlePrintReceipt(selectedPayment.id)}
              >
                Print
              </Button>
              <Button
                startIcon={<Email />}
                onClick={() => handleEmailReceipt(selectedPayment.id)}
              >
                Email
              </Button>
              {selectedPayment.status === 'completed' && (
                <Button
                  color="error"
                  onClick={() => handleRefund(selectedPayment.id)}
                >
                  Refund
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Payments;
